import {
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import type { i18n as I18n } from 'i18next'
import {
  getDashboardUserProfileBootstrap,
  getPublishedBooks,
} from '@services/firebase'
import { useDashboardState } from '@features/dashboard/store/useDashboardState'
import {
  mapLegacySelfStudyLessons,
  type LegacySelfStudyLesson,
} from './legacy-self-study-mapper'
import {
  getCurrentProgramBook,
  prepareLegacyLessonsForBook,
} from './dashboard-route-model'
import { normalizeDashboardLanguageValue } from '@features/dashboard/store/dashboard-state'
import {
  getLearningPerformanceTimestamp,
  logLearningPerformanceTrace,
} from '@shared/learning/learning-performance-trace'

interface UseDashboardBootstrapOptions {
  readonly i18n: Pick<I18n, 'changeLanguage'>
  readonly requiresLearningBootstrap: boolean
}

export interface DashboardBootstrapState {
  readonly errorMessage: string | null
  readonly legacySelfStudyLessons: readonly LegacySelfStudyLesson[]
  readonly setErrorMessage: Dispatch<SetStateAction<string | null>>
  readonly setLegacySelfStudyLessons: Dispatch<
    SetStateAction<readonly LegacySelfStudyLesson[]>
  >
}

export function useDashboardBootstrap({
  i18n,
  requiresLearningBootstrap,
}: UseDashboardBootstrapOptions): DashboardBootstrapState {
  const { actions } = useDashboardState()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [legacySelfStudyLessons, setLegacySelfStudyLessons] = useState<
    readonly LegacySelfStudyLesson[]
  >([])

  useEffect(() => {
    let isMounted = true

    async function bootstrap() {
      const bootstrapStartedAt = getLearningPerformanceTimestamp()

      try {
        actions.setBootstrapping(true)
        setErrorMessage(null)

        const [profileBootstrap, books] = await Promise.all([
          getDashboardUserProfileBootstrap(),
          getPublishedBooks(),
        ])
        const profileAndBooksReadyAt =
          bootstrapStartedAt === null ? null : performance.now()
        const { profile } = profileBootstrap
        const currentBook = getCurrentProgramBook(
          books,
          profile.current_learning_program,
        )

        if (!isMounted) {
          return
        }

        actions.setMyUser(profile)
        actions.setBooks(books)
        actions.setBook(currentBook)

        if (profile.language_default) {
          void i18n.changeLanguage(
            normalizeDashboardLanguageValue(profile.language_default),
          )
        }

        const deferredProfilePromise = profileBootstrap.deferredProfileFields
          .then((deferredFields) => {
            const deferredProfileReadyAt = getLearningPerformanceTimestamp()
            const resolvedProfile = {
              ...profile,
              ...deferredFields,
            }

            if (isMounted) {
              actions.mergeMyUser(deferredFields)
            }

            if (bootstrapStartedAt !== null && deferredProfileReadyAt !== null) {
              logLearningPerformanceTrace('dashboard.bootstrap.deferredProfile', {
                countDailyActivities: deferredFields.count_daily_activities,
                dailyActivities: deferredFields.daily_activities.length,
                durationFromBootstrapMs: Math.round(deferredProfileReadyAt - bootstrapStartedAt),
                hasPersonalization: Boolean(deferredFields.personalization),
                hasSelfStudyChapterLearning: Boolean(deferredFields.selfstudy_chapter_learning),
              })
            }

            return resolvedProfile
          })
        void deferredProfilePromise
          .catch((error) => {
            if (isMounted) {
              setErrorMessage(
                error instanceof Error
                  ? error.message
                  : 'Dashboard profile details failed to load.',
              )
            }
          })

        if (!requiresLearningBootstrap) {
          actions.setLessons([])
          actions.setLessonsBookId(null)
          actions.setLesson(null)
          actions.setLessonsErrorMessage(null)
          actions.setLessonsLoading(false)
          setLegacySelfStudyLessons([])

          if (bootstrapStartedAt !== null && profileAndBooksReadyAt !== null) {
            logLearningPerformanceTrace('dashboard.bootstrap.shellCriticalPath', {
              books: books.length,
              bootstrapStartedAtMs: Math.round(bootstrapStartedAt),
              hasCurrentBook: Boolean(currentBook),
              profileAndBooksMs: Math.round(profileAndBooksReadyAt - bootstrapStartedAt),
            })
          }

          return
        }

        const { lessons, mappedLessons, progressSource } = await prepareLegacyLessonsForBook(
          currentBook,
          profile,
        )
        const lessonMetadataReadyAt =
          bootstrapStartedAt === null ? null : performance.now()

        if (!isMounted) {
          return
        }

        actions.setLessons(lessons)
        actions.setLessonsBookId(currentBook?.id ?? null)
        actions.setLesson(lessons[0] ?? null)
        setLegacySelfStudyLessons(mappedLessons)

        if (bootstrapStartedAt !== null && profileAndBooksReadyAt !== null && lessonMetadataReadyAt !== null) {
          logLearningPerformanceTrace('dashboard.bootstrap.criticalPath', {
            bootstrapStartedAtMs: Math.round(bootstrapStartedAt),
            lessons: lessons.length,
            lessonMetadataMs: Math.round(lessonMetadataReadyAt - profileAndBooksReadyAt),
            profileAndBooksMs: Math.round(profileAndBooksReadyAt - bootstrapStartedAt),
            progressSource,
          })
        }

        void deferredProfilePromise.then((resolvedProfile) => {
          if (isMounted) {
            setLegacySelfStudyLessons(mapLegacySelfStudyLessons({
              lessons,
              profile: resolvedProfile,
            }))
          }
        })
      } catch (error) {
        if (isMounted) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : 'Legacy compatibility dashboard failed to load.',
          )
        }
      } finally {
        if (isMounted) {
          actions.setBootstrapping(false)
        }
      }
    }

    void bootstrap()

    return () => {
      isMounted = false
    }
  }, [actions, i18n, requiresLearningBootstrap])

  return {
    errorMessage,
    legacySelfStudyLessons,
    setErrorMessage,
    setLegacySelfStudyLessons,
  }
}
